import { useMemo, useState } from 'react'
import { useUnit } from '@/modules/units/contexts/UnitContext'
import type { UnitContext } from '@/modules/units/contexts/UnitContextTypes'
import type { Locale } from '@/shared/contexts/TranslationContextTypes'
import { buildTranslationsSync } from '@/lib/i18n/translationUtils'

const defaultLocale: Locale =
  (import.meta.env.VITE_DEFAULT_LOCALE as Locale) || 'pt-BR'

function getInitialLocale(): Locale {
  if (typeof window !== 'undefined') {
    const storedLocale = localStorage.getItem('preferredLocale') as Locale | null

    if (storedLocale && ['pt-BR', 'en-US', 'es-ES'].includes(storedLocale)) {
      return storedLocale
    }
  }

  return defaultLocale
}

export function useTranslationState() {
  const [locale, setLocaleState] = useState<Locale>(getInitialLocale)

  const fixedContext = (
    (import.meta.env.VITE_PUBLIC_APP_CONTEXT as UnitContext) ||
    (import.meta.env.NEXT_PUBLIC_APP_CONTEXT as UnitContext) ||
    null
  )
  const defaultContext: UnitContext = fixedContext || 'farm'

  let unitCtx: ReturnType<typeof useUnit> | null = null

  try {
    unitCtx = useUnit()
  } catch {
    unitCtx = null
  }

  // A fixed-context app must keep its own dictionary even before its first
  // workspace exists or when the profile still points to a unit from another app.
  const unitContext = (
    fixedContext && fixedContext !== 'unit'
      ? fixedContext
      : unitCtx?.contextKey || defaultContext
  ) as UnitContext

  const translations = useMemo(
    () => buildTranslationsSync(locale, unitContext),
    [locale, unitContext],
  )

  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale)

    if (typeof window !== 'undefined') {
      localStorage.setItem('preferredLocale', newLocale)
    }
  }

  return {
    locale,
    unitContext,
    translations,
    setLocale,
    unitLoading: unitCtx?.isLoading ?? false,
  }
}
